/* eslint-disable no-alert */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makePost, makePatch, signUpload } from "@culturehq/client";
import { Form, ModalDialog, StringField, SubmitButton, NumberField, SelectField, TextField, Button, Icon, classnames } from "@culturehq/components";
import { useDepartments, useLocations, useInterests, useSkills, useValues, useCompanies, useProfile } from "app/context";

import { useDoor, useFieldsLabels, useTryCatch } from "app/hooks";
import { canViewCompaniesField, canViewSkillsField } from "app/permissions";
import { Trans, errorService, getDepartmentFieldCategory, getCompanyFieldCategory, t, isRTX } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import tagsTypes, { getCategoryLabel, getIcon } from "constants/tagsTypes";
import SelectImage from "components/common/SelectImage";
import TrimVideo from "components/stories/TrimVideo";
import VideoOptions from "components/common/VideoOptions";
import ProgressLine from "components/admin/analytics/ProgressLine";
import ChqModalDialog from "components/common/ChqModalDialog";
import StoryPromptOptions from "./StoryPromptOptions";
import ApproveForJobMappingButton from "../stories/ApproveForJobMappingButton";
import SubPromptModal from "./SubPromptModal";
import StatusChangeConfirmModal from "./StatusChangeConfirmModal";
import KEYS from "../../../stories/post-stories-v2/constants";

const VIDEO_TYPE = "video";

const makeSelectOptions = (entities, labelKey, valueKey, categoryIcon, icon, category) => {
  const selectOptions = [];

  Object.keys(entities).forEach(entityId => {
    const { [labelKey]: label, [valueKey]: value } = entities[entityId];
    selectOptions.push({ label, value, categoryIcon, icon, category });
  });

  return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
};

const useSelectOptions = () => {
  const { profile } = useProfile();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { interests, onAllInterestsRequested, onAddInterest } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { skills, onAllSkillsRequested } = useSkills();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();

  useEffect(
    () => {
      if (profile && canViewCompaniesField(profile.organization)) {
        onAllCompaniesRequested();
      }
    }, [profile, onAllCompaniesRequested]
  );

  useEffect(
    () => {
      if (profile && canViewSkillsField(profile.organization)) {
        onAllSkillsRequested();
      }
    }, [profile, onAllSkillsRequested]
  );

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllDepartmentsRequested();
    onAllOrganizationValuesRequested();
  }, [onAllDepartmentsRequested, onAllInterestsRequested,
    onAllLocationsRequested, onAllOrganizationValuesRequested]);

  const interestsOptions = makeSelectOptions(interests, "name", "id", "heart-ol", "heart", tagsTypes.INTERESTS);
  const locationOptions = makeSelectOptions(locations, "name", "id", "loc-ol", "ios-location", tagsTypes.LOCATIONS);
  const departmentOptions = makeSelectOptions(departments, "name", "id", "ios-people-outline", "ios-people", getDepartmentFieldCategory(profile.organization.name));
  const orgValuesOptions = makeSelectOptions(organizationValues, "name", "id", "diamond", "diamond-filled", tagsTypes.ORG_VALUES, [], undefined);

  let options = [
    ...orgValuesOptions,
    ...interestsOptions,
    ...locationOptions,
    ...departmentOptions
  ];

  if (profile && canViewCompaniesField(profile.organization)) {
    const companyOptions = makeSelectOptions(companies, "name", "id", "building", "building-filled", getCompanyFieldCategory(profile.organization.name));
    options = [...options, ...companyOptions];
  }

  if (profile && canViewSkillsField(profile.organization)) {
    const skillOptions = makeSelectOptions(skills, "name", "id", "hammer", "hammer-filled", tagsTypes.SKILLS);
    options = [...options, ...skillOptions];
  }

  return {
    options,
    onAddInterest,
    indexes: {
      companies,
      departments,
      interests,
      locations,
      skills,
      organizationValues
    }
  };
};

const Title = ({ storyQuestion, duplicate }) => {
  if (duplicate) return <Trans>Duplicate Story Trend</Trans>;
  return storyQuestion
    ? <Trans>Edit Story Trend</Trans>
    : <Trans>Create New Story Trend</Trans>;
};

const ButtonLabel = ({ storyQuestion }) => {
  if (storyQuestion) return <Trans>Save Story Trend</Trans>;

  return <Trans>Create Story Trend</Trans>;
};

const NewStoryQuestionModal = ({
  children, storyQuestion, onCreate, onUpdate, selectedOptions, onReplace, duplicate
}) => {
  const { profile: { organization: { name: orgName } } } = useProfile();
  const [changed, setChanged] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [valuesToSubmit, setValuesToSubmit] = useState(null);

  const onChange = useCallback(() => {
    setChanged(true);
  }, [setChanged]);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the trend modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        onClose();
        restartState();
      }
    },
    [changed, onClose, restartState]
  );
  const labels = useFieldsLabels();
  const history = useHistory();
  const { open, onOpen, onClose: onCloseModal } = useDoor(false);
  const { options: selectOptions, onAddInterest } = useSelectOptions();
  const [customOptions, setCustomOptions] = useState([]);
  const allOptions = [...selectOptions, ...customOptions];

  const [tags, setTags] = useState(selectedOptions?.map(({ entity, entityType }) => (
    {
      category: getCategoryLabel(entityType, labels),
      value: entity?.id,
      label: entity?.name,
      categoryIcon: getIcon(entityType)
    }
  )));
  const [instructionalVideo, setInstructionalVideo] = useState();
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState();
  const [subprompts, setSubprompts] = useState([]);
  const [bannerImage, setBannerImage] = useState();
  const [originalBannerImage, setOriginalBannerImage] = useState();
  const [error, setError] = useState();
  const [trimming, setTrimming] = useState(false);
  const [videoDimension, setVideoDimension] = useState({ height: null, width: null });
  const [progress, setProgress] = useState();
  const [recordedVideo, setRecordedVideo] = useState();
  const [gamificationValue, setGamificationValue] = useState(50);
  const [mappersValueIds, setMappersValueIds] = useState([]);
  const GAMIFICATION_MIN = 0;
  const GAMIFICATION_MAX = 1000;

  const videoDimensionRef = useRef();
  videoDimensionRef.current = videoDimension;

  const selectFieldRef = useRef(null);
  const [selectFieldKey, setSelectFieldKey] = useState(0);

  useEffect(() => {
    const handleClickOutside = event => {
      if (selectFieldRef.current && !selectFieldRef.current.contains(event.target)) {
        setSelectFieldKey(prev => prev + 1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const restartState = useCallback(
    () => {
      if (!storyQuestion) {
        setGamificationValue(50);
        setInstructionalVideo();
        setBannerImage();
        setOriginalBannerImage();
        setSubprompts([]);
        setTags();
        setMappersValueIds({});
        setCustomOptions([]);
      } else {
        setBannerImage(storyQuestion.imageUrl);
        setInstructionalVideo(storyQuestion.media?.url);
        setVideoThumbnailUrl(storyQuestion.media?.thumbnail);
        if (storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt") {
          setSubprompts([storyQuestion]);
        } else {
          setSubprompts(storyQuestion.childStoryQuestions);
        }
      }
      setChanged(false);
    },
    [storyQuestion]
  );

  useEffect(
    () => {
      if (storyQuestion) {
        setGamificationValue(storyQuestion.points ?? 50);
        setBannerImage(storyQuestion.imageUrl);
        setInstructionalVideo(storyQuestion.media?.url);
        setVideoThumbnailUrl(storyQuestion.media?.thumbnail);
        setMappersValueIds(storyQuestion.externalMapperValueAssociations.map(
          mapperValueAssocaition => (mapperValueAssocaition.externalMapperValue.id)
        ));
        if (storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt") {
          setSubprompts([storyQuestion]);
        } else {
          setSubprompts(storyQuestion.childStoryQuestions);
        }
      }
    }, [storyQuestion]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onClose = () => {
    setProgress();
    onCloseModal();
  };

  const [{ trying, error: tryCatchError }, handleSubmit] = useTryCatch(
    async ({ points, question, summary, visibility, status }) => {
      setError();
      if (!bannerImage) {
        setError(t("Banner image is required"));
        return;
      }
      if (subprompts.length === 0) {
        setError(t("At least one sub prompt is required"));
        return;
      }

      // Handle other status changes with window.confirm for now
      if (status === "hidden" && storyQuestion?.answers > 0 && storyQuestion?.status !== "hidden") {
        const confirmMessage = t(`Any associated stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be removed from the Culture Page. Would you like to proceed?`);
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "active" && storyQuestion?.answers > 0 && storyQuestion?.status === "archived") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be moved out of Archive in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "archived" && storyQuestion?.answers > 0 && storyQuestion?.status !== "archived") {
        const confirmMessage = t(`The stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be archived automatically. Do you want to continue?`);
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      let processVideo = false;
      let mediaUpload = 2;
      let localProgress = 0;
      let newImageUrl = null;

      if (instructionalVideo) {
        mediaUpload = 3;
      }

      if (bannerImage && bannerImage.size) {
        newImageUrl = await signUpload(bannerImage, mediaProgress => {
          localProgress = (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
      } else {
        localProgress = (100 / mediaUpload);
        newImageUrl = bannerImage;
      }

      let newOriginalImageUrl = null;
      if (originalBannerImage && originalBannerImage.size) {
        newOriginalImageUrl = await signUpload(originalBannerImage, mediaProgress => {
          localProgress = (100 / mediaUpload) + (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
      } else {
        localProgress += (100 / mediaUpload);
        newOriginalImageUrl = originalBannerImage;
      }

      let newInstructionalVideo = null;
      if (instructionalVideo && instructionalVideo.size) {
        newInstructionalVideo = await signUpload(instructionalVideo, mediaProgress => {
          localProgress = 66 + (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
        processVideo = true;
      } else {
        if (instructionalVideo) {
          localProgress += (100 / mediaUpload);
        }
        newInstructionalVideo = instructionalVideo;
      }

      function getMediaUrl() {
        if (!instructionalVideo) return undefined;
        if (process.env.NODE_ENV === "development") return KEYS.DEVELOPMENT_VIDEO_URL;

        return newInstructionalVideo;
      }

      let body = {
        imageUrl: newImageUrl,
        mappersValueIds,
        originalImageUrl: newOriginalImageUrl,
        points,
        question,
        tags: tags?.map(tag => {
          if (tag.isNew) {
            return tag.value;
          }
          if (tag.category) {
            if (isRTX(orgName) && tag.category === tagsTypes.BUSINESSES) {
              return {
                ...tag,
                category: tagsTypes.COMPANIES
              };
            }
            const departmentCategories = [
              tagsTypes.BUSINESSES, tagsTypes.PROFESSIONS, tagsTypes.JOBFUNCTIONS
            ];
            return {
              ...tag,
              category: departmentCategories.includes(tag.category)
                ? tagsTypes.DEPARTMENTS : tag.category
            };
          }

          return tag;
        }),
        summary,
        mediaUrl: getMediaUrl(),
        height: videoDimensionRef.current.height,
        width: videoDimensionRef.current.width,
        web: true,
        processVideo: processVideo || trimming,
        recordedVideo,
        visibility,
        status
      };

      if (storyQuestion) {
        body = {
          ...body,
          subprompts: subprompts.map(subprompt => (
            { ...subprompt, instructions: subprompt.instructions || subprompt.summary }
          )),
          storyQuestionType: storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt" ? "child_prompt" : undefined,
          trimming
        };
      } else {
        body = { ...body, subprompts };
      }

      try {
        await new Promise((resolve, reject) => {
          setTimeout(async () => {
            let response;
            if (storyQuestion && !duplicate) {
              try {
                restartState();
                response = await makePatch(`/story_questions/${storyQuestion.id}`, body);
                if (response.storyQuestion) {
                  if (onReplace) {
                    onReplace(response.storyQuestion, storyQuestion.id);
                  } else {
                    onUpdate(response.storyQuestion);
                  }
                }
                onClose();
                resolve(response);
              } catch (error) {
                reject(error);
              }
            } else {
              try {
                response = await makePost("/story_questions", body);
                if (response.storyQuestion) {
                  restartState();
                  history.push(`/stories/trend/${response.storyQuestion.id}`);
                } else {
                  onClose();
                }
                resolve(response);
              } catch (error) {
                reject(error);
              }
            }

            if (response.storyQuestion) {
              response.storyQuestion.storyQuestionTags.filter(
                tag => tag.entityType.toLowerCase() === tagsTypes.INTEREST
              ).forEach(tag => onAddInterest(tag.entity));
            }
          }, 3000);
        });
        setError();
        onClose();
      } catch (error) {
        setProgress();
        setError(t("Something went wrong, please try again or contact support!"));
        errorService.error(error);
      }
    },
    [duplicate, history, storyQuestion, onClose, onCreate, onReplace, onUpdate, tags, bannerImage,
      instructionalVideo, subprompts, trimming, recordedVideo, mappersValueIds]
  );

  if (tryCatchError && tryCatchError.status !== 422) {
    setError(tryCatchError);
    errorService.error(tryCatchError);
  }

  const onSelectedGroupsChange = value => {
    onChange();
    const newCustomOptions = [];
    const porcessedTags = value?.map(tag => {
      // is a newly added tag
      if (typeof tag === "string") {
        const newOption = {
          category: tagsTypes.INTERESTS,
          value: tag,
          label: tag,
          categoryIcon: "heart-ol",
          icon: "heart",
          isNew: true
        };
        newCustomOptions.push(newOption);

        return newOption;
      }

      return tag;
    });
    setCustomOptions(prev => [...prev, ...newCustomOptions]);
    setTags(porcessedTags || []);
  };

  const handleOnMediaChange = async ({
    media,
    thumb,
    isRecordedVideo
  }) => {
    setInstructionalVideo(media);
    onChange();
    if (media.type.startsWith(VIDEO_TYPE)) {
      setRecordedVideo(isRecordedVideo);
      if (thumb) {
        setVideoThumbnailUrl(URL.createObjectURL(thumb));
      }
    }
  };

  const getSelectedMedia = () => {
    if (instructionalVideo?.type?.startsWith(VIDEO_TYPE)) {
      return URL.createObjectURL(instructionalVideo);
    }

    return instructionalVideo;
  };

  const handleVideoTrimmed = (video, editingVideo) => {
    setInstructionalVideo(video);
    onChange();
    setTrimming(editingVideo);
  };

  const handleRemoveVideo = () => {
    setRecordedVideo(undefined);
    setInstructionalVideo(undefined);
    onChange();
  };

  const handleBannerImageSelected = (originalImage, image) => {
    setOriginalBannerImage(originalImage);
    setBannerImage(image);
    onChange();
  };

  const handleNewSubprompt = subprompt => {
    onChange();
    setSubprompts([...subprompts, subprompt]);
  };

  const handleEditSubprompt = subprompt => {
    const newPrompts = subprompts;
    newPrompts[subprompt.index] = subprompt;
    setSubprompts(newPrompts);
    onChange();
  };

  const handleRemoveSubprompt = index => {
    onChange();
    const newPrompts = subprompts;
    newPrompts.splice(index, 1);
    setSubprompts(newPrompts);
  };

  const handleSelectedJobMappings = ({ mappersValueIds }) => {
    setMappersValueIds(mappersValueIds);
  };

  const handleSubmitWithConfirmation = useCallback(async formValues => {
    if (storyQuestion?.status === "hidden" && formValues.status === "active") {
      setValuesToSubmit(formValues);
      setShowConfirmModal(true);
      return;
    }
    await handleSubmit(formValues);
  }, [storyQuestion, handleSubmit]);

  const handleCancelSubmit = useCallback(() => {
    setShowConfirmModal(false);
    setValuesToSubmit(null);
  }, []);

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose} className="story-question-modal new-story-question-modal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Title storyQuestion={storyQuestion} duplicate={duplicate} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Form
              onSubmit={handleSubmitWithConfirmation}
              initialValues={{
                typesAllowed: "all",
                points: gamificationValue,
                visibility: "private_access",
                status: "active",
                ...storyQuestion
              }}
            >
              {error && <ErrorMessage error={error} />}
              <StringField name="question" autoFocus required placeholder={t("Write your Story Trend title")} onChange={onChange}>
                <Trans>Title</Trans>
              </StringField>
              <div className={classnames("Summary-Container", { "Summary-Container__Video": !!instructionalVideo })}>
                <TextField
                  name="summary"
                  placeholder={t("Share details about what you're looking for and the purpose of this trend so users know why they should contribute")}
                  required
                  onChange={onChange}
                >
                  <Trans>Summary</Trans>
                </TextField>
                <div className="Video-Container">
                  <div className="Video-Container__Wrapper">
                    {instructionalVideo ? (
                      <>
                        <video
                          className="chq-ffd--video"
                          poster={videoThumbnailUrl}
                          src={getSelectedMedia()}
                          disablePictureInPicture
                          controlsList="nodownload"
                          onLoadedMetadata={e => {
                            if (videoDimension.height !== e.target.videoHeight
                              && videoDimension.width !== e.target.videoWidth) {
                              setVideoDimension({
                                height: e.target.videoHeight,
                                width: e.target.videoWidth
                              });
                            }
                          }}
                        />
                        <TrimVideo
                          storyQuestion={storyQuestion}
                          src={getSelectedMedia()}
                          thumbnail={videoThumbnailUrl}
                          onVideoTrimmed={handleVideoTrimmed}
                          fillParent
                        />
                        <Icon className="close-icon" icon="close" onClick={handleRemoveVideo} />
                      </>
                    ) : (
                      <div className="Empty-Video">
                        <Icon icon="video-marketing" />
                        <Trans>Add welcome or instructional video</Trans>
                      </div>
                    )}
                  </div>
                  <VideoOptions handleOnMediaChange={handleOnMediaChange} />
                </div>
              </div>
              <SelectImage
                aspectRatio={14 / 6}
                onImageSelected={handleBannerImageSelected}
                imageToShow={bannerImage}
              />
              <StoryPromptOptions
                onDelete={handleRemoveSubprompt}
                onUpdate={handleEditSubprompt}
                prompts={subprompts}
                onChange={onChange}
              />
              <SubPromptModal
                parentStoryQuestion={storyQuestion}
                onUpdate={() => {
                  onChange();
                  onUpdate();
                }}
                onCreate={handleNewSubprompt}
              >
                {onTrigger => (
                  <Button className={classnames("Subprompt-Add_Btn", { EmptySubPrompts: subprompts.length === 0 })} inverted onClick={onTrigger}>
                    <Icon icon="ios-plus" />
                    <Trans>
                      {
                        subprompts.length === 0
                          ? "Add Your First Prompt"
                          : "Add Another Prompt"
                      }
                    </Trans>
                  </Button>
                )}
              </SubPromptModal>
              <NumberField
                name="points"
                placeholder={t("Choose how much users should earn for contributing")}
                required
                onChange={value => {
                  const numericValue = parseInt(value, 10);
                  if (!Number.isNaN(numericValue)) {
                    if ((numericValue >= GAMIFICATION_MIN)
                      && (numericValue <= GAMIFICATION_MAX)) {
                      setGamificationValue(numericValue);
                      onChange();
                    }
                  }
                }}
                min={GAMIFICATION_MIN}
                max={GAMIFICATION_MAX}
                value={gamificationValue.toString()}
              >
                <Trans>Gamification</Trans>
              </NumberField>
              <div ref={selectFieldRef} key={selectFieldKey}>
                <SelectField
                  className="share-with-field"
                  creatable
                  name="allOptions"
                  options={allOptions}
                  placeholder={t(`Select default ${labels.departments.plural.toLowerCase()}, locations, interests, etc.`)}
                  multiple
                  onChange={onSelectedGroupsChange}
                  value={tags}
                >
                  <Trans>Default Tags</Trans>
                </SelectField>
              </div>
              <SelectField
                name="visibility"
                required
                options={[
                  { value: "private_access", label: t("Private") },
                  { value: "public_access", label: t("Public") }
                ]}
                onChange={onChange}
              >
                <Trans>Visibility</Trans>
              </SelectField>
              <SelectField
                name="status"
                required
                options={[
                  { value: "active", label: t("Active") },
                  { value: "paused", label: t("Paused") },
                  { value: "hidden", label: t("Hidden") },
                  { value: "archived", label: t("Archived") }
                ]}
                onChange={onChange}
              >
                <Trans>Status</Trans>
              </SelectField>
              {progress && (
                <div className="progressline-trend">
                  <ProgressLine
                    className="story-progressbar"
                    color="#79b17d"
                    valueNow={progress}
                    valueMax={100}
                  />
                </div>
              )}
              <div className="Footer-Buttons">
                <SubmitButton primary disabled={trying}>
                  {submitting => ((submitting || trying) ? (
                    <Trans>Submitting...</Trans>
                  ) : (
                    <ButtonLabel storyQuestion={storyQuestion} />
                  ))}
                </SubmitButton>
                <ApproveForJobMappingButton
                  mappersValueIds={mappersValueIds}
                  onUpdate={value => {
                    handleSelectedJobMappings(value);
                  }}
                  storyQuestion={storyQuestion}
                />
              </div>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      {showConfirmModal && (
        <StatusChangeConfirmModal
          onCancelSubmit={handleCancelSubmit}
          onSubmit={handleSubmit}
          showConfirmModal={showConfirmModal}
          values={valuesToSubmit}
          message={(
            <Trans>
              In changing to Active status, any associated stories may now be
              selected for your Culture Page in the Admin Review tab.
              Also, the prompts in this trend must be activated to be visible.
            </Trans>
          )}
        />
      )}
    </>
  );
};

export default NewStoryQuestionModal;
